import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { Permission, RolePermission } from "../types";
import { permissionsService } from "../../../../../services/permissions";
import { capitalize } from "~/utils";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";

interface PermissionsTabProps {}

const roles = [
  { value: "SUPER_ADMIN", label: "Super Admin", color: "red" },
  { value: "ADMIN", label: "Admin", color: "blue" },
  { value: "LEAD", label: "Lead", color: "orange" },
  { value: "STAFF", label: "Staff", color: "green" },
  { value: "CLIENT", label: "Client", color: "default" },
];

// const resourceGroups = {
//   "User Management": ["users", "staff", "clients"],
//   Operations: ["bookings", "jobs", "inventory", "service_tickets", "tasks"],
//   Financial: ["invoicing", "billing", "payroll"],
//   Configuration: [
//     "backend_setup",
//     "staff_roles",
//     "vessels",
//     "storage_locations",
//     "services",
//     "admin_charges",
//     "fabrication",
//     "vendors",
//   ],
//   Reports: ["reports", "analytics"],
//   System: ["notifications", "file_management"],
// };

type RoleData = {
  value: string;
  label: string;
  color: string;
  count: number;
};

export const PermissionsTab: React.FC<PermissionsTabProps> = () => {
  const { user } = useAuth();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [rolePermissions, setRolePermissions] = useState<{
    [key: string]: RolePermission[];
  }>({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    loadPermissionsData();
  }, []);

  const loadPermissionsData = async () => {
    try {
      setInitialLoading(true);

      // Load all permissions and role permissions
      const [permissions, rolePerms] = await Promise.all([
        permissionsService.getAllPermissions(),
        permissionsService.getAllRolePermissions(),
      ]);

      setAllPermissions(permissions);
      setRolePermissions(rolePerms);
    } catch (error) {
      console.error("Failed to load permissions:", error);
      message.error("Failed to load permissions data");
    } finally {
      setInitialLoading(false);
    }
  };

  const initializePermissions = async () => {
    try {
      setLoading(true);
      await permissionsService.initializeDefaultPermissions();
      message.success("Default permissions initialized successfully");
      await loadPermissionsData();
    } catch (error) {
      console.error("Failed to initialize permissions:", error);
      message.error("Failed to initialize permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = (role: string) => {
    setSelectedRole(role);
    const permissions = rolePermissions[role] || [];
    form.setFieldsValue({
      permissions: permissions
        .filter((p) => p.isGranted)
        .map((p) => p.permissionName),
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      await permissionsService.updateRolePermissions({
        role: selectedRole,
        permissionNames: values.permissions,
      });

      message.success(
        `Permissions updated for ${
          roles.find((r) => r.value === selectedRole)?.label
        }`
      );

      setIsModalVisible(false);
      await loadPermissionsData(); // Reload data
    } catch (error) {
      console.error("Failed to update permissions:", error);
      message.error("Failed to update permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const columns: ColumnsType<RoleData> = [
    {
      title: "Role",
      dataIndex: "value",
      key: "role",
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <Tag color={record.color}>{record.label}</Tag>
        </Space>
      ),
    },
    {
      title: "Permissions Count",
      dataIndex: "count",
      key: "count",
      render: (count) => <span>{count} permissions</span>,
    },
    {
      title: "Description",
      key: "description",
      render: (_, record) => {
        const descriptions = {
          SUPER_ADMIN:
            "Full access to all systems including backend configuration",
          ADMIN: "Administrative access to most features except backend setup",
          LEAD: "Supervisory access with team management and approval permissions",
          STAFF: "Operational access to assigned tasks and jobs",
          CLIENT: "Limited access to view their own bookings and inventory",
        };
        return descriptions[record.value as keyof typeof descriptions];
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPermissions(record.value)}
        >
          Edit Permissions
        </Button>
      ),
    },
  ];

  const roleData: RoleData[] = roles.map((role) => ({
    ...role,
    count: rolePermissions[role.value]?.filter((p) => p.isGranted).length || 0,
  }));

  const getAvailablePermissions = () => {
    // Group permissions by resource for better organization
    const groupedPermissions: { [key: string]: string[] } = {};

    allPermissions.forEach((permission) => {
      if (!groupedPermissions[permission.resource]) {
        groupedPermissions[permission.resource] = [];
      }
      groupedPermissions[permission.resource].push(permission.name);
    });

    return groupedPermissions;
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Role Permissions</h3>
        <p className="text-gray-500 mb-4">
          Configure access levels for different user roles. Each role has
          specific permissions that determine what features and data users can
          access.
        </p>
        <Space>
          {user && user.role === Role.SUPER_ADMIN && (
            <Button
              type="primary"
              onClick={initializePermissions}
              loading={loading}
            >
              Initialize Default Permissions
            </Button>
          )}
          <Button onClick={loadPermissionsData}>Refresh Data</Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={roleData}
          rowKey="value"
          pagination={false}
          className="bg-white"
        />
      </Card>

      <Modal
        title={
          <Space>
            <SettingOutlined />
            Edit Permissions -
            {roles.find((r) => r.value === selectedRole)?.label}
          </Space>
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        width={800}
        bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="permissions"
            label="Permissions"
            rules={[
              {
                required: true,
                message: "Please select at least one permission",
              },
            ]}
          >
            <Checkbox.Group style={{ width: "100%" }}>
              {Object.entries(getAvailablePermissions()).map(
                ([resource, permissionNames]) => (
                  <div key={resource} style={{ marginBottom: 16 }}>
                    <Divider orientation="left" orientationMargin="0">
                      <strong>{capitalize(resource)}</strong>
                    </Divider>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: 8,
                      }}
                    >
                      {permissionNames.map((permissionName) => (
                        <Checkbox
                          key={permissionName}
                          value={permissionName}
                          className="mb-4"
                        >
                          {permissionName}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                )
              )}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
